import { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Chip, Divider, FormControl, FormControlLabel, FormHelperText,
  IconButton, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import ScheduleService from '../../../shared/services/schedule/schedule.service';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import AnalyticReportService from '../../../shared/services/analytic-report/analytic-report.service';
import DistributionListService from '../../../shared/services/distribution-list/distribution-list.service';
import ThirdPartyConnectionDetailsService from '../../../modules/third-party-connection/shared-services/third-party-connection-details/third-party-connection.service';
import CloudEmissionsService from '../../../modules/third-party-connection/shared-services/cloud-emissions/cloud-emissions.service';
import { CLOUD_EMISSIONS_PROVIDERS, CLOUD_PROVIDER_NAMES } from '../../../modules/third-party-connection/shared-services/cloud-emissions/cloud-emissions.model';
import {
  AccountingSyncProvider,
  CloudSyncProvider,
  ICreateSchedule, ICreateScheduleAction, ISchedule,
  ScheduleActionType, ScheduleFrequency, ScheduleType,
} from '../../../shared/services/schedule/schedule.model';
import { ISurvey } from '../../../shared/services/survey/survey.model';
import { IAnalyticReport } from '../../../shared/services/analytic-report/analytic-report.model';
import { IDistributionList } from '../../../shared/services/distribution-list/distribution-list.model';
import { IThirdPartyConnectionDetailsCompany } from '../../../modules/third-party-connection/shared-services/third-party-connection-details/third-party-connection-details.model';
import { notifyError } from '../../../shared/utils/notify-error';
import { useDirtyBaseline } from '../../../hooks/use-dirty-baseline';

/** A financial provider the org has an active connection to, with its companies. */
interface SyncProviderOption {
  code: AccountingSyncProvider;
  name: string;
  companies: IThirdPartyConnectionDetailsCompany[];
}

const DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const ACTION_TYPE_LABELS: Partial<Record<ScheduleActionType, string>> = {
  send_survey_invitations: 'Send survey invitations',
  // send_report_email: 'Send report email',
  send_analytics_report: 'Send analytics report',
  sync_accounting: 'Sync accounting data',
  sync_cloud_emissions: 'Sync cloud carbon figures',
};

const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
})();

const BASE_TIMEZONES = [
  'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Australia/Sydney', 'Asia/Singapore', 'Asia/Tokyo',
];

const COMMON_TIMEZONES = BASE_TIMEZONES.includes(BROWSER_TZ)
  ? BASE_TIMEZONES
  : [BROWSER_TZ, ...BASE_TIMEZONES];

interface ActionRow {
  actionType: ScheduleActionType;
  surveyId?: string;
  templateCode?: string;
  analyticReportId?: string;
  distributionListIds?: string[];
  additionalEmails?: string;
  customEmailText?: string;
  // sync_accounting and sync_cloud_emissions
  provider?: AccountingSyncProvider | CloudSyncProvider;
  companyId?: string;
  companyName?: string;
  autoCommit?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  editSchedule?: ISchedule | null;
  /** Pre-populate the actions list when creating a new schedule. */
  defaultActions?: ActionRow[];
  /** Called with the newly created schedule after a successful create (not edit). */
  onCreated?: (schedule: ISchedule) => void;
}

export const ScheduleDialog = ({ open, onClose, successHandler, editSchedule, defaultActions, onCreated }: Props) => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const scheduleService = new ScheduleService();
  const surveyAdminService = SurveyAdminService.getInstance();
  const analyticReportService = new AnalyticReportService();
  const distributionListService = new DistributionListService();
  const thirdPartyConnectionDetailsService = new ThirdPartyConnectionDetailsService();
  const cloudEmissionsService = new CloudEmissionsService();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('one_off');
  const [frequency, setFrequency] = useState<ScheduleFrequency>('weekly');
  // One-off timing
  const [runAtDate, setRunAtDate] = useState<Moment | null>(null);
  const [runAtTime, setRunAtTime] = useState<Moment | null>(moment().hour(9).minute(0).second(0));
  // Recurring timing
  const [dayOfWeek, setDayOfWeek] = useState<number>(1);
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [runTime, setRunTime] = useState<Moment | null>(moment().hour(9).minute(0).second(0));
  const [customIntervalValue, setCustomIntervalValue] = useState<number>(2);
  const [customIntervalUnit, setCustomIntervalUnit] = useState<'hours' | 'days'>('hours');
  const [timezone, setTimezone] = useState(BROWSER_TZ);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  // Actions
  const [actions, setActions] = useState<ActionRow[]>([]);
  // Edit-only: re-activate on save
  const [setActiveOnSave, setSetActiveOnSave] = useState(true);
  // Async state
  const [surveys, setSurveys] = useState<ISurvey[]>([]);
  const [analyticReports, setAnalyticReports] = useState<IAnalyticReport[]>([]);
  const [distributionLists, setDistributionLists] = useState<IDistributionList[]>([]);
  // sync_accounting: financial providers the org is actively connected to (+ companies)
  const [syncProviders, setSyncProviders] = useState<SyncProviderOption[]>([]);
  const [syncProvidersLoaded, setSyncProvidersLoaded] = useState(false);
  const [loadingSyncProviders, setLoadingSyncProviders] = useState(false);
  // sync_cloud_emissions: cloud connectors the org has connected
  const [cloudProviders, setCloudProviders] = useState<{ code: CloudSyncProvider, name: string }[]>([]);
  const [cloudProvidersLoaded, setCloudProvidersLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dirty tracking over every user-editable field (this form is hand-rolled state, so
  // there is no formik.dirty). Moments serialise to ISO strings via toJSON. The
  // initialisation effect below calls rebaseline() after seeding.
  const { isDirty, rebaseline } = useDirtyBaseline({
    name, description, scheduleType, frequency, runAtDate, runAtTime,
    dayOfWeek, dayOfMonth, runTime, customIntervalValue, customIntervalUnit,
    timezone, endDate, actions, setActiveOnSave,
  });

  // Load surveys, analytic reports, and distribution lists for action dropdowns
  useEffect(() => {
    if (open) {
      surveyAdminService.getSurveys({ status: 'ACTIVE', perPage: 100, page: 0 })
        .then(r => setSurveys(r.rows))
        .catch(() => {});
      analyticReportService.getAll()
        .then(r => setAnalyticReports(r))
        .catch(() => {});
      distributionListService.getAll()
        .then(r => setDistributionLists(r))
        .catch(() => {});
    }
  }, [open]);

  // Lazily load the financial providers the org is actively connected to — only
  // when a sync_accounting action is present, since this hits the provider APIs.
  const loadSyncProviders = async () => {
    if (syncProvidersLoaded || loadingSyncProviders) return;
    setLoadingSyncProviders(true);
    try {
      const available = await thirdPartyConnectionDetailsService.getAvailableConnections();
      const financial = available.filter(c => c.details === 'financial');
      const withCompanies = await Promise.all(
        financial.map(async c => {
          try {
            const companies = await thirdPartyConnectionDetailsService.getConnectedCompanies(c.code);
            return { code: c.code as AccountingSyncProvider, name: c.name, companies: companies || [] };
          } catch {
            return { code: c.code as AccountingSyncProvider, name: c.name, companies: [] };
          }
        }),
      );
      // Only providers with at least one connected company count as "active".
      setSyncProviders(withCompanies.filter(p => p.companies.length > 0));
    } catch {
      setSyncProviders([]);
    } finally {
      setSyncProvidersLoaded(true);
      setLoadingSyncProviders(false);
    }
  };

  useEffect(() => {
    if (open && actions.some(a => a.actionType === 'sync_accounting')) {
      loadSyncProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, actions]);

  // Cloud connectors the org has actually connected, loaded only when a cloud sync action
  // is on the schedule.
  useEffect(() => {
    if (!open || cloudProvidersLoaded || !actions.some(a => a.actionType === 'sync_cloud_emissions')) return;
    setCloudProvidersLoaded(true);
    Promise.all(CLOUD_EMISSIONS_PROVIDERS.map(code =>
      cloudEmissionsService.getStatus(code).then(status => (status.connected ? code : null)).catch(() => null),
    ))
      .then(codes => setCloudProviders(codes
        .filter((code): code is CloudSyncProvider => !!code)
        .map(code => ({ code, name: CLOUD_PROVIDER_NAMES[code] }))))
      .catch(() => setCloudProviders([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, actions, cloudProvidersLoaded]);

  // Initialise form when dialog opens
  useEffect(() => {
    if (open) {
      if (editSchedule) {
        setName(editSchedule.name);
        setDescription(editSchedule.description || '');
        setScheduleType(editSchedule.scheduleType);
        setFrequency(editSchedule.frequency || 'weekly');
        setTimezone(editSchedule.timezone || 'UTC');
        if (editSchedule.runAt) {
          const m = moment(editSchedule.runAt);
          setRunAtDate(m);
          setRunAtTime(m);
        } else {
          setRunAtDate(null);
          setRunAtTime(moment().hour(9).minute(0).second(0));
        }
        setDayOfWeek(editSchedule.dayOfWeek ?? 1);
        setDayOfMonth(editSchedule.dayOfMonth ?? 1);
        if (editSchedule.runTime) {
          const [hh, mm] = editSchedule.runTime.split(':').map(Number);
          setRunTime(moment().hour(hh).minute(mm).second(0));
        }
        if (editSchedule.customIntervalMinutes) {
          if (editSchedule.customIntervalMinutes % (24 * 60) === 0) {
            setCustomIntervalValue(editSchedule.customIntervalMinutes / (24 * 60));
            setCustomIntervalUnit('days');
          } else {
            setCustomIntervalValue(editSchedule.customIntervalMinutes / 60);
            setCustomIntervalUnit('hours');
          }
        }
        setEndDate(editSchedule.endDate ? moment(editSchedule.endDate) : null);
        setActions((editSchedule.scheduleActions || []).map(a => ({
          actionType: a.actionType as ScheduleActionType,
          surveyId: (a.actionPayload as any)?.surveyId,
          templateCode: (a.actionPayload as any)?.templateCode,
          analyticReportId: (a.actionPayload as any)?.analyticReportId,
          distributionListIds: (a.actionPayload as any)?.distributionListIds || [],
          additionalEmails: ((a.actionPayload as any)?.additionalEmails || []).join('\n'),
          customEmailText: (a.actionPayload as any)?.customEmailText,
          provider: (a.actionPayload as any)?.provider,
          companyId: (a.actionPayload as any)?.companyId,
          companyName: (a.actionPayload as any)?.companyName,
          autoCommit: (a.actionPayload as any)?.autoCommit ?? false,
        })));
      } else {
        // Reset
        setName('');
        setDescription('');
        setScheduleType('one_off');
        setFrequency('weekly');
        setRunAtDate(null);
        setRunAtTime(moment().hour(9).minute(0).second(0));
        setDayOfWeek(1);
        setDayOfMonth(1);
        setRunTime(moment().hour(9).minute(0).second(0));
        setCustomIntervalValue(2);
        setCustomIntervalUnit('hours');
        setTimezone(BROWSER_TZ);
        setEndDate(null);
        setActions(defaultActions ?? []);
      }
      setSetActiveOnSave(true);
      setErrors({});
      setIsSubmitting(false);
      // Re-fetch active connections each time the dialog opens.
      setSyncProviders([]);
      setSyncProvidersLoaded(false);
      setLoadingSyncProviders(false);
      setCloudProviders([]);
      setCloudProvidersLoaded(false);
      // Full snapshot is safe here: this effect runs synchronously in the open
      // transition, before the user can have touched the form.
      rebaseline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (scheduleType === 'one_off') {
      if (!runAtDate) {
        errs.runAt = 'Date is required';
      } else {
        // Combine date + time and check it is in the future
        const combined = runAtDate.clone();
        if (runAtTime) combined.hour(runAtTime.hour()).minute(runAtTime.minute()).second(0).millisecond(0);
        if (combined.isSameOrBefore(moment())) {
          errs.runAt = 'Date and time must be in the future';
        }
      }
    }
    if (scheduleType === 'recurring' && frequency === 'custom') {
      const mins = customIntervalUnit === 'hours' ? customIntervalValue * 60 : customIntervalValue * 24 * 60;
      if (mins < 60) errs.customInterval = 'Minimum interval is 1 hour';
    }
    actions.forEach((a, idx) => {
      if (a.actionType === 'send_survey_invitations' && !a.surveyId) {
        errs[`action_${idx}_surveyId`] = 'Survey is required';
      }
      if (a.actionType === 'send_analytics_report') {
        if (!a.analyticReportId) {
          errs[`action_${idx}_analyticReportId`] = 'Analytics report is required';
        }
        const hasDistList = (a.distributionListIds || []).length > 0;
        const hasAdditional = (a.additionalEmails || '').split(/[\n,]/).map(e => e.trim()).filter(Boolean).length > 0;
        if (!hasDistList && !hasAdditional) {
          errs[`action_${idx}_recipients`] = 'At least one distribution list or email address is required';
        }
      }
      if (a.actionType === 'sync_accounting') {
        if (!a.provider) {
          errs[`action_${idx}_provider`] = 'A connected provider is required';
        }
        if (!a.companyId) {
          errs[`action_${idx}_company`] = 'A company is required';
        }
      }
      if (a.actionType === 'sync_cloud_emissions' && !a.provider) {
        errs[`action_${idx}_provider`] = 'A connected cloud provider is required';
      }
    });
    // One cloud sync per provider per schedule.
    const seenCloud = new Set<string>();
    actions.forEach((a, idx) => {
      if (a.actionType !== 'sync_cloud_emissions' || !a.provider) return;
      if (seenCloud.has(a.provider)) {
        errs[`action_${idx}_provider`] = 'This provider is already synced by another action on this schedule';
      }
      seenCloud.add(a.provider);
    });
    // Disallow two identical accounting syncs (same provider + company) on one schedule.
    const seenSyncKeys = new Map<string, number>();
    actions.forEach((a, idx) => {
      if (a.actionType === 'sync_accounting' && a.provider && a.companyId) {
        const key = `${a.provider}::${a.companyId}`;
        if (seenSyncKeys.has(key)) {
          errs[`action_${idx}_company`] = 'This provider and company is already synced by another action on this schedule';
        } else {
          seenSyncKeys.set(key, idx);
        }
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildRunAt = (): string | undefined => {
    if (scheduleType !== 'one_off' || !runAtDate) return undefined;
    const d = runAtDate.clone();
    if (runAtTime) {
      d.hour(runAtTime.hour()).minute(runAtTime.minute()).second(0).millisecond(0);
    }
    return d.toISOString();
  };

  const buildRunTime = (): string | undefined => {
    if (!runTime) return '09:00';
    return `${String(runTime.hour()).padStart(2, '0')}:${String(runTime.minute()).padStart(2, '0')}`;
  };

  const buildCustomIntervalMinutes = (): number => {
    return customIntervalUnit === 'hours'
      ? customIntervalValue * 60
      : customIntervalValue * 24 * 60;
  };

  const buildActions = (): ICreateScheduleAction[] => {
    return actions.map(a => {
      if (a.actionType === 'send_survey_invitations') {
        return {
          actionType: a.actionType,
          actionPayload: {
            surveyId: a.surveyId || '',
            customEmailText: a.customEmailText || 'Please complete the employee commuting survey by clicking the button below.',
          },
        };
      }
      if (a.actionType === 'send_report_email') {
        return { actionType: a.actionType, actionPayload: { templateCode: a.templateCode || '', distributionListIds: [] } };
      }
      if (a.actionType === 'send_analytics_report') {
        return {
          actionType: a.actionType,
          actionPayload: {
            analyticReportId: a.analyticReportId || '',
            distributionListIds: a.distributionListIds || [],
            additionalEmails: (a.additionalEmails || '')
              .split(/[\n,]/)
              .map((e: string) => e.trim())
              .filter(Boolean),
            customEmailText: a.customEmailText || 'Your report is attached to this email.',
          },
        };
      }
      if (a.actionType === 'sync_cloud_emissions') {
        return {
          actionType: a.actionType,
          actionPayload: {
            provider: a.provider as CloudSyncProvider,
            autoCommit: !!a.autoCommit,
          },
        };
      }
      if (a.actionType === 'sync_accounting') {
        return {
          actionType: a.actionType,
          actionPayload: {
            provider: a.provider as AccountingSyncProvider,
            companyId: a.companyId || null,
            companyName: a.companyName,
            autoCommit: !!a.autoCommit,
          },
        };
      }
      return { actionType: a.actionType, actionPayload: {} };
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const data: ICreateSchedule = {
        name: name.trim(),
        description: description.trim() || undefined,
        scheduleType,
        timezone,
        actions: buildActions(),
      };

      if (scheduleType === 'one_off') {
        data.runAt = buildRunAt();
      } else {
        data.frequency = frequency;
        if (frequency === 'weekly') {
          data.dayOfWeek = dayOfWeek;
          data.runTime = buildRunTime();
        } else if (frequency === 'monthly') {
          data.dayOfMonth = dayOfMonth;
          data.runTime = buildRunTime();
        } else if (frequency === 'custom') {
          data.customIntervalMinutes = buildCustomIntervalMinutes();
        }
        if (endDate) data.endDate = endDate.toISOString();
      }

      if (editSchedule) {
        await scheduleService.update(editSchedule.id, { ...data, active: setActiveOnSave ? true : undefined });
        // Sync actions: delete existing, re-create
        for (const existing of editSchedule.scheduleActions || []) {
          await scheduleService.deleteAction(editSchedule.id, existing.id);
        }
        for (const a of buildActions()) {
          await scheduleService.createAction(editSchedule.id, a);
        }
      } else {
        const created = await scheduleService.create(data);
        onCreated?.(created);
      }

      userNotificationsContext.openSnackbar({
        open: true,
        message: editSchedule ? 'Schedule updated' : 'Schedule created',
        backgroundColor: 'primary.dark',
      });
      successHandler();
    } catch (e: any) {
      notifyError(userNotificationsContext.openSnackbar, e, 'saving the schedule');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addAction = () => {
    setActions(prev => [...prev, { actionType: 'send_survey_invitations' }]);
  };

  const updateAction = (idx: number, patch: Partial<ActionRow>) => {
    setActions(prev => prev.map((a, i) => i === idx ? { ...a, ...patch } : a));
  };

  const removeAction = (idx: number) => {
    setActions(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DialogWrapper
        open={open}
        onClose={onClose}
        title={editSchedule ? 'Edit schedule' : 'New schedule'}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
        isSubmitting={isSubmitting}
        disableSaveButton={isSubmitting}
        isDirty={isDirty}
        maxWidth='sm'
      >
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {/* ── Basic info ─────────────────────────────────────────── */}
          <TextField
            label='Name'
            value={name}
            onChange={e => setName(e.target.value)}
            size='small'
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            label='Description (optional)'
            value={description}
            onChange={e => setDescription(e.target.value)}
            size='small'
            fullWidth
            multiline
            rows={2}
          />

          <Divider />

          {/* ── Timing ────────────────────────────────────────────── */}
          <Typography variant='subtitle2' fontWeight={600}>Timing</Typography>

          <FormControl size='small' fullWidth>
            <InputLabel>Schedule type</InputLabel>
            <Select
              label='Schedule type'
              value={scheduleType}
              onChange={e => setScheduleType(e.target.value as ScheduleType)}
            >
              <MenuItem value='one_off'>One-off (runs once)</MenuItem>
              <MenuItem value='recurring'>Recurring (repeats)</MenuItem>
            </Select>
          </FormControl>

          {/* One-off: date + time */}
          {scheduleType === 'one_off' && (
            <Stack direction='row' spacing={1.5}>
              <DatePicker
                label='Date'
                value={runAtDate}
                onChange={v => { setRunAtDate(v); if (errors.runAt) setErrors(e => ({ ...e, runAt: '' })); }}
                disablePast
                format='DD/MM/YYYY'
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!errors.runAt,
                    helperText: errors.runAt,
                  },
                }}
              />
              <TimePicker
                label='Time'
                value={runAtTime}
                onChange={v => setRunAtTime(v)}
                ampm={false}
                minTime={runAtDate && runAtDate.isSame(moment(), 'day') ? moment() : undefined}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Stack>
          )}

          {/* Recurring: frequency */}
          {scheduleType === 'recurring' && (
            <>
              <FormControl size='small' fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  label='Frequency'
                  value={frequency}
                  onChange={e => setFrequency(e.target.value as ScheduleFrequency)}
                >
                  <MenuItem value='weekly'>Weekly</MenuItem>
                  <MenuItem value='monthly'>Monthly</MenuItem>
                  <MenuItem value='custom'>Custom interval</MenuItem>
                </Select>
              </FormControl>

              {frequency === 'weekly' && (
                <Stack direction='row' spacing={1.5}>
                  <FormControl size='small' sx={{ flex: 1 }}>
                    <InputLabel>Day of week</InputLabel>
                    <Select
                      label='Day of week'
                      value={dayOfWeek}
                      onChange={e => setDayOfWeek(Number(e.target.value))}
                    >
                      {DAY_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TimePicker
                    label='Time'
                    value={runTime}
                    onChange={v => setRunTime(v)}
                    ampm={false}
                    slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                  />
                </Stack>
              )}

              {frequency === 'monthly' && (
                <Stack direction='row' spacing={1.5}>
                  <FormControl size='small' sx={{ flex: 1 }}>
                    <InputLabel>Day of month</InputLabel>
                    <Select
                      label='Day of month'
                      value={dayOfMonth}
                      onChange={e => setDayOfMonth(Number(e.target.value))}
                    >
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TimePicker
                    label='Time'
                    value={runTime}
                    onChange={v => setRunTime(v)}
                    ampm={false}
                    slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                  />
                </Stack>
              )}

              {frequency === 'custom' && (
                <Stack direction='row' spacing={1.5} alignItems='flex-start'>
                  <Typography variant='body2' sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}>Every</Typography>
                  <TextField
                    size='small'
                    type='number'
                    inputProps={{ min: 1 }}
                    value={customIntervalValue}
                    onChange={e => setCustomIntervalValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    sx={{ width: 80 }}
                    error={!!errors.customInterval}
                    helperText={errors.customInterval}
                  />
                  <FormControl size='small' sx={{ minWidth: 100 }}>
                    <Select
                      value={customIntervalUnit}
                      onChange={e => setCustomIntervalUnit(e.target.value as 'hours' | 'days')}
                    >
                      <MenuItem value='hours'>Hours</MenuItem>
                      <MenuItem value='days'>Days</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant='caption' color='text.secondary' sx={{ alignSelf: 'center' }}>
                    (min. 1 hour)
                  </Typography>
                </Stack>
              )}

              <FormControl size='small' fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  label='Timezone'
                  value={timezone}
                  onChange={e => setTimezone(e.target.value)}
                >
                  {COMMON_TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
                </Select>
              </FormControl>

              <DatePicker
                label='End date (optional)'
                value={endDate}
                onChange={v => setEndDate(v)}
                disablePast
                format='DD/MM/YYYY'
                slotProps={{ textField: { size: 'small', fullWidth: true, helperText: 'Schedule stops after this date' } }}
              />
            </>
          )}

          <Divider />

          {/* ── Actions ───────────────────────────────────────────── */}
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='subtitle2' fontWeight={600}>Actions</Typography>
            <Button size='small' startIcon={<AddOutlined />} onClick={addAction} disabled={isSubmitting}>
              Add action
            </Button>
          </Stack>

          {actions.length === 0 && (
            <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
              No actions: this schedule will fire but do nothing.
            </Typography>
          )}

          {actions.map((a, idx) => (
            <Box key={idx} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Stack spacing={1.5}>
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                  <FormControl size='small' sx={{ flex: 1, mr: 1 }}>
                    <InputLabel>Action type</InputLabel>
                    <Select
                      label='Action type'
                      value={a.actionType}
                      onChange={e => updateAction(idx, { actionType: e.target.value as ScheduleActionType, surveyId: undefined, templateCode: undefined, analyticReportId: undefined, distributionListIds: [], additionalEmails: undefined, provider: undefined, companyId: undefined, companyName: undefined, autoCommit: false })}
                    >
                      {(Object.keys(ACTION_TYPE_LABELS) as ScheduleActionType[]).map(t => (
                        <MenuItem key={t} value={t}>{ACTION_TYPE_LABELS[t]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton size='small' onClick={() => removeAction(idx)} disabled={isSubmitting}>
                    <DeleteOutline fontSize='small' />
                  </IconButton>
                </Stack>

                {a.actionType === 'send_survey_invitations' && (
                  <>
                    <FormControl size='small' fullWidth error={!!errors[`action_${idx}_surveyId`]}>
                      <InputLabel>Survey</InputLabel>
                      <Select
                        label='Survey'
                        value={a.surveyId || ''}
                        onChange={e => updateAction(idx, { surveyId: e.target.value })}
                      >
                        {surveys.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                      </Select>
                      {errors[`action_${idx}_surveyId`] && (
                        <FormHelperText>{errors[`action_${idx}_surveyId`]}</FormHelperText>
                      )}
                    </FormControl>

                    <TextField
                      size='small'
                      label='Email body text'
                      value={a.customEmailText ?? 'Please complete the employee commuting survey by clicking the button below.'}
                      onChange={e => updateAction(idx, { customEmailText: e.target.value })}
                      fullWidth
                      multiline
                      rows={3}
                      helperText='Text shown in the email body'
                    />
                  </>
                )}

                {a.actionType === 'send_report_email' && (
                  <TextField
                    size='small'
                    label='Template code'
                    value={a.templateCode || ''}
                    onChange={e => updateAction(idx, { templateCode: e.target.value })}
                    fullWidth
                    helperText='The transactional email template code (e.g. monthly_report)'
                  />
                )}

                {a.actionType === 'send_analytics_report' && (
                  <>
                    <FormControl size='small' fullWidth error={!!errors[`action_${idx}_analyticReportId`]}>
                      <InputLabel>Analytics report</InputLabel>
                      <Select
                        label='Analytics report'
                        value={a.analyticReportId || ''}
                        onChange={e => updateAction(idx, { analyticReportId: e.target.value })}
                      >
                        {analyticReports.map(r => (
                          <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                        ))}
                      </Select>
                      {errors[`action_${idx}_analyticReportId`] && (
                        <FormHelperText>{errors[`action_${idx}_analyticReportId`]}</FormHelperText>
                      )}
                    </FormControl>

                    <FormControl size='small' fullWidth error={!!errors[`action_${idx}_recipients`]}>
                      <InputLabel>Distribution lists</InputLabel>
                      <Select
                        label='Distribution lists'
                        multiple
                        value={a.distributionListIds || []}
                        onChange={e => updateAction(idx, { distributionListIds: e.target.value as string[] })}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map(id => {
                              const list = distributionLists.find(l => l.id === id);
                              return <Chip key={id} label={list?.listName ?? id} size='small' />;
                            })}
                          </Box>
                        )}
                      >
                        {distributionLists.map(l => (
                          <MenuItem key={l.id} value={l.id}>{l.listName}</MenuItem>
                        ))}
                      </Select>
                      {errors[`action_${idx}_recipients`] && (
                        <FormHelperText>{errors[`action_${idx}_recipients`]}</FormHelperText>
                      )}
                    </FormControl>

                    <TextField
                      size='small'
                      label='Additional email addresses (optional)'
                      value={a.additionalEmails || ''}
                      onChange={e => updateAction(idx, { additionalEmails: e.target.value })}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder={'jane@example.com\njohn@example.com'}
                      helperText='One email address per line'
                    />
                  </>
                )}

                {a.actionType === 'send_analytics_report' && (
                  <TextField
                    size='small'
                    label='Email body text'
                    value={a.customEmailText || 'Your report is attached to this email.'}
                    onChange={e => updateAction(idx, { customEmailText: e.target.value })}
                    fullWidth
                    multiline
                    rows={3}
                    helperText='Text shown in the email body'
                  />
                )}

                {a.actionType === 'sync_cloud_emissions' && (
                  <>
                    <FormControl size='small' fullWidth error={!!errors[`action_${idx}_provider`]}>
                      <InputLabel>Cloud provider</InputLabel>
                      <Select
                        label='Cloud provider'
                        value={cloudProviders.some(p => p.code === a.provider) ? a.provider : ''}
                        onChange={e => updateAction(idx, { provider: e.target.value as CloudSyncProvider })}
                      >
                        {cloudProviders.length === 0 && <MenuItem disabled value=''>No connected cloud providers</MenuItem>}
                        {cloudProviders.map(p => (
                          <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors[`action_${idx}_provider`]
                          || 'Only cloud providers your organisation is connected to are listed.'}
                      </FormHelperText>
                    </FormControl>

                    <Typography variant='caption' color='text.secondary'>
                      When this schedule fires, the vendor's monthly carbon figures are fetched for every open
                      report period. Months needing a decision wait on the integration page.
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!a.autoCommit}
                          onChange={e => updateAction(idx, { autoCommit: e.target.checked })}
                          size='small'
                        />
                      }
                      label={
                        <Stack>
                          <Typography variant='body2'>Book months that need no decision</Typography>
                          <Typography variant='caption' color='text.secondary'>
                            Months with no invoice to pair are booked on their own, and invoice pairings are
                            booked once you have confirmed one by hand. You receive an email summary after each run.
                          </Typography>
                        </Stack>
                      }
                    />
                  </>
                )}

                {a.actionType === 'sync_accounting' && (() => {
                  const providerOpt = syncProviders.find(p => p.code === a.provider);
                  const companies = providerOpt?.companies || [];
                  return (
                    <>
                      <FormControl size='small' fullWidth error={!!errors[`action_${idx}_provider`]}>
                        <InputLabel>Provider</InputLabel>
                        <Select
                          label='Provider'
                          value={syncProviders.some(p => p.code === a.provider) ? a.provider : ''}
                          onChange={e => {
                            const code = e.target.value as AccountingSyncProvider;
                            const opt = syncProviders.find(p => p.code === code);
                            const onlyCompany = opt && opt.companies.length === 1 ? opt.companies[0] : undefined;
                            updateAction(idx, {
                              provider: code,
                              companyId: onlyCompany?.companyId,
                              companyName: onlyCompany?.companyName,
                            });
                          }}
                        >
                          {loadingSyncProviders && <MenuItem disabled value=''>Loading connections…</MenuItem>}
                          {!loadingSyncProviders && syncProviders.length === 0 && (
                            <MenuItem disabled value=''>No active accounting connections</MenuItem>
                          )}
                          {syncProviders.map(p => (
                            <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors[`action_${idx}_provider`]
                            || 'Only providers your organisation is connected to are listed.'}
                        </FormHelperText>
                      </FormControl>

                      {a.provider && (
                        <FormControl size='small' fullWidth error={!!errors[`action_${idx}_company`]}>
                          <InputLabel>Company</InputLabel>
                          <Select
                            label='Company'
                            value={companies.some(c => c.companyId === a.companyId) ? a.companyId : ''}
                            onChange={e => {
                              const company = companies.find(c => c.companyId === e.target.value);
                              updateAction(idx, { companyId: company?.companyId, companyName: company?.companyName });
                            }}
                          >
                            {companies.map(c => (
                              <MenuItem key={c.companyId} value={c.companyId}>{c.companyName}</MenuItem>
                            ))}
                          </Select>
                          {errors[`action_${idx}_company`] && (
                            <FormHelperText>{errors[`action_${idx}_company`]}</FormHelperText>
                          )}
                        </FormControl>
                      )}

                      <>
                          <Typography variant='caption' color='text.secondary'>
                            When this schedule fires, transactions are imported into your open report period as
                            outstanding records to review on the Financial page.
                          </Typography>

                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!a.autoCommit}
                                onChange={e => updateAction(idx, { autoCommit: e.target.checked })}
                                size='small'
                              />
                            }
                            label={
                              <Stack>
                                <Typography variant='body2'>Auto-commit imported records</Typography>
                                <Typography variant='caption' color='text.secondary'>
                                  Automatically commit lines that were categorised. Anything needing review is
                                  left outstanding, and you receive an email summary after each run.
                                </Typography>
                              </Stack>
                            }
                          />
                        </>
                    </>
                  );
                })()}
              </Stack>
            </Box>
          ))}

          {/* Edit-only: set active on save ─────────────────────────── */}
          {editSchedule && (
            <>
              <Divider />
              <FormControlLabel
                control={
                  <Switch
                    checked={setActiveOnSave}
                    onChange={e => setSetActiveOnSave(e.target.checked)}
                    size='small'
                  />
                }
                label={
                  <Stack>
                    <Typography variant='body2'>Set to active on save</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {setActiveOnSave
                        ? 'Schedule will be activated when saved'
                        : 'Schedule will remain in its current state'}
                    </Typography>
                  </Stack>
                }
              />
            </>
          )}
        </Stack>
      </DialogWrapper>
    </LocalizationProvider>
  );
};
